import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../lib/firebase';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { assertWorkspaceInvariant } from '../utils/workspaceInvariant';
import { HTML_PUBLISH_SETTINGS_ID, isHtmlPagesEnabledFromData } from '../utils/htmlPublish';

/**
 * Contract:
 * - 内部編集系APIは workspaceId を唯一のキーとして扱う
 * - 公開配信系APIで publishId を受ける場合、必ず workspace.publishId と一致検証する
 */
const assertPublishIdBoundToWorkspace = async (
  workspaceId: string,
  publishId: string,
  source: string
) => {
  const pid = String(publishId || '').trim();
  if (!pid) {
    throw new Error(`[public-pages-contract] publishId is required (source=${source})`);
  }
  const wsRef = doc(db, 'workspaces', workspaceId);
  const wsSnap = await getDoc(wsRef);
  const wsPublishId = wsSnap.exists() ? String((wsSnap.data() as any)?.publishId || '').trim() : '';
  if (!wsPublishId || wsPublishId !== pid) {
    throw new Error(
      `[public-pages-contract] publishId mismatch (source=${source}) workspaceId=${workspaceId} expected=${wsPublishId || '(none)'} actual=${pid}`
    );
  }
};

export const publishWorkspacePublicPage = async (workspaceId: string) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.publishWorkspacePublicPage' });
  const fn = httpsCallable(functions, 'publishWorkspacePublicPageFn');
  const res = await fn({ workspaceId });
  return res.data as { publishId: string; slug: string };
};

export const unpublishWorkspacePublicPage = async (workspaceId: string) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.unpublishWorkspacePublicPage' });
  const fn = httpsCallable(functions, 'unpublishWorkspacePublicPageFn');
  const res = await fn({ workspaceId });
  return res.data as { success: boolean; changed: boolean };
};

export const switchPublicPageRelease = async (workspaceId: string, publishId: string, releaseId: string) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.switchPublicPageRelease' });
  await assertPublishIdBoundToWorkspace(workspaceId, publishId, 'publicPages.switchPublicPageRelease');
  const fn = httpsCallable(functions, 'switchPublicPageReleaseFn');
  const res = await fn({ workspaceId, publishId, releaseId });
  return res.data as { ok: boolean };
};

export const purgePublicPageRelease = async (workspaceId: string, publishId: string, releaseId: string) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.purgePublicPageRelease' });
  await assertPublishIdBoundToWorkspace(workspaceId, publishId, 'publicPages.purgePublicPageRelease');
  const fn = httpsCallable(functions, 'purgePublicPageReleaseFn');
  const res = await fn({ workspaceId, publishId, releaseId });
  return res.data as { ok: boolean; deleted: number };
};


export const copyEmbeddingsToPublic = async (workspaceId: string) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.copyEmbeddingsToPublic' });
  try {
    const fn = httpsCallable(functions, 'copyEmbeddingsToPublicFn');

    const res = await fn({ workspaceId });

    return res.data as { 
      success: boolean; 
      copied: number; 
      skipped: number;
      publicBoardCount: number;
      totalSourceEmbeddings: number;
      finalDestCount: number; 
      message: string; 
    };
  } catch (error: any) {
    console.error('🔗 copyEmbeddingsToPublic API: エラー発生', {
      workspaceId,
      error,
      errorMessage: error?.message,
      errorCode: error?.code,
      errorDetails: error?.details,
      timestamp: new Date().toISOString()
    });
    throw error; // エラーを再スロー
  }
};

export const revalidatePublicPages = async (workspaceId: string) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.revalidatePublicPages' });
  const fn = httpsCallable(functions, 'revalidatePublicPagesFn');
  const res = await fn({ workspaceId });
  return res.data as { publishId: string; count: number; ok: number; ng: number };
};

export const getHtmlPagesEnabled = async (workspaceId: string): Promise<boolean> => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.getHtmlPagesEnabled' });
  const snap = await getDoc(doc(db, 'publicPages', workspaceId, 'settings', HTML_PUBLISH_SETTINGS_ID));
  return isHtmlPagesEnabledFromData(snap.exists() ? snap.data() : undefined);
};

export const setHtmlPagesEnabled = async (workspaceId: string, enabled: boolean) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.setHtmlPagesEnabled' });
  const fn = httpsCallable(functions, 'setHtmlPagesEnabledFn');
  const res = await fn({ workspaceId, enabled });
  return res.data as {
    enabled: boolean;
    workspaceId: string;
    revalidate: { publishId: string; ok: boolean; status?: number; error?: string } | null;
  };
};

/**
 * ノート単位でページ軸（Webページとして公開するか）を切り替える。
 * 知識軸（AIの回答に使うか）は変えないので、Webページだけ止めても AI は答え続ける。
 */
export const setPagesWebEnabled = async (
  workspaceId: string,
  pageIds: string[],
  enabled: boolean
) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.setPagesWebEnabled' });
  const fn = httpsCallable(functions, 'setPagesWebEnabledFn');
  const res = await fn({ workspaceId, pageIds, enabled });
  return res.data as {
    success: boolean;
    enabled: boolean;
    updatedCount: number;
    updatedPageIds?: string[];
    skippedPageIds?: string[];
    embeddingsError?: string | null;
    publishJob: {
      jobId?: string;
      status: 'started' | 'already_running' | 'no_pages';
      message: string;
      totalPages?: number;
      totalBatches?: number;
    } | null;
    revalidate: { publishId: string; ok: boolean; status?: number; error?: string } | null;
  };
};

export const generatePublicPageContent = async (workspaceId: string, pageId?: string) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.generatePublicPageContent' });
  const fn = httpsCallable(functions, 'generatePublicPageContentFn');
  const res = await fn({ workspaceId, pageId });
  return res.data as { 
    success: boolean; 
    processedCount: number; 
    successCount: number; 
    errorCount: number; 
    results: Array<{ pageId: string; success: boolean; error?: string }>; 
    message: string; 
  };
};

// バッチ公開ジョブを開始（対象は「Webページとして公開する」がONで HTML 未生成のノート）
export const startBatchPublishContent = async (workspaceId: string, pageIds?: string[]) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.startBatchPublishContent' });
  const fn = httpsCallable(functions, 'startBatchPublishContentFn');
  const res = await fn(pageIds ? { workspaceId, pageIds } : { workspaceId });
  return res.data as {
    jobId?: string;
    status: string;
    totalPages?: number;
    totalBatches?: number;
    message: string;
  };
};

// バッチ公開ジョブの進捗を取得
export const getBatchPublishProgress = async (jobId: string) => {
  const fn = httpsCallable(functions, 'getBatchPublishProgressFn');
  const res = await fn({ jobId });
  return res.data as {
    jobId: string;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    totalPages: number;
    processedPages: number;
    successCount: number;
    errorCount: number;
    currentBatch: number;
    totalBatches: number;
    progress: number;
    errors: Array<{ pageId: string; error: string }>;
    createdAt: any;
    updatedAt: any;
    completedAt?: any;
    userId: string; // ジョブの作成者ID
  };
};

export const checkExistingBatchJobs = async (workspaceId: string) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.checkExistingBatchJobs' });
  const fn = httpsCallable(functions, 'checkExistingBatchJobsFn');
  const res = await fn({ workspaceId });
  return res.data as {
    found: boolean;
    jobId?: string;
    status?: string;
    totalPages?: number;
    processedPages?: number;
    successCount?: number;
    errorCount?: number;
    currentBatch?: number;
    totalBatches?: number;
    progress?: number;
    createdAt?: any;
    updatedAt?: any;
    userId?: string; // ジョブの作成者ID
  };
};

// バッチ非公開処理のAPI関数
export const startBatchUnpublishContent = async (workspaceId: string, pageIds: string[]) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.startBatchUnpublishContent' });
  const fn = httpsCallable(functions, 'startBatchUnpublishContentFn');
  const res = await fn({ workspaceId, pageIds });
  return res.data as {
    jobId?: string;
    status: string;
    totalPages?: number;
    totalBatches?: number;
    message: string;
  };
};

export const getBatchUnpublishProgress = async (jobId: string) => {
  const fn = httpsCallable(functions, 'getBatchUnpublishProgressFn');
  const res = await fn({ jobId });
  return res.data as {
    jobId: string;
    status: 'queued' | 'processing' | 'completed' | 'failed';
    totalPages: number;
    processedPages: number;
    successCount: number;
    errorCount: number;
    currentBatch: number;
    totalBatches: number;
    progress: number;
    errors: Array<{ pageId: string; error: string }>;
    createdAt: any;
    updatedAt: any;
    completedAt?: any;
    userId: string; // ジョブの作成者ID
  };
};

export const checkExistingUnpublishJobs = async (workspaceId: string) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.checkExistingUnpublishJobs' });
  const fn = httpsCallable(functions, 'checkExistingUnpublishJobsFn');
  const res = await fn({ workspaceId });
  return res.data as {
    found: boolean;
    jobId?: string;
    status?: string;
    totalPages?: number;
    processedPages?: number;
    successCount?: number;
    errorCount?: number;
    currentBatch?: number;
    totalBatches?: number;
    progress?: number;
    createdAt?: any;
    updatedAt?: any;
    userId?: string; // ジョブの作成者ID
  };
};

/** 公開AI検索のメインタイトル（welcome）用アイコン */
export interface ContentTitleIconSettings {
  /** false のとき表示しない（URLは保持）。未設定時は URL があれば表示 */
  enabled?: boolean;
  url: string;
  size: number;
  color: string;
  colorEnabled?: boolean;
  /** タイトルテキストとの位置関係 */
  position: 'before' | 'after' | 'above' | 'below';
}

// 公開サイト設定の型定義
export interface PublicSiteSettings {
  favicon?: string; // ファビコンのURL
  tabTitle?: string; // タブタイトル
  headerTitle?: string; // ヘッダータイトル
  headerDescription?: string; // ヘッダー説明
  headerBackgroundColor?: string; // ヘッダー背景色
  headerHeight?: number; // ヘッダー高さ(px)
  /** 公開ページにヘッダーを出すか。未設定は OFF */
  headerEnabled?: boolean;
  headerHasShadow?: boolean; // ヘッダー影の有無
  headerIsFixed?: boolean; // ヘッダー固定表示の有無
  title?: string; // ヘッダーに表示するタイトルテキスト
  titleColor?: string; // タイトルの色
  mainColor?: string; // メインカラー（アクセント色）
  titleFontSize?: number; // タイトルのフォントサイズ(px)
  logoUrl?: string; // ロゴ画像のURL
  logoHeight?: number; // ロゴ高さ(px)
  logoLinkUrl?: string; // ロゴクリック時のリンク先
  logoOpenInNewTab?: boolean; // ロゴリンクを新しいタブで開くか
  linkText?: string; // リンクテキスト
  linkColor?: string; // リンク色
  linkTextColor?: string; // 本文中のリンクテキスト色
  linkUrl?: string; // リンクURL
  linkOpenInNewTab?: boolean; // リンクを新しいタブで開くか
  linkBackgroundColor?: string; // リンク背景色
  linkBorderColor?: string; // リンク外枠色
  linkPadding?: number; // リンクパディング(px)
  linkBorderRadius?: number; // リンク角の丸さ(px)
  contentMaxWidth?: number; // コンテンツ表示エリア最大横幅(px)
  links?: Array<{
    id: string;
    text: string;
    color: string;
    url: string;
    openInNewTab: boolean;
    backgroundColor: string;
    borderColor: string;
    padding: number;
    borderRadius: number;
  }>; // 複数リンク設定
  contentTitle?: string; // コンテンツタイトル
  /** メインタイトル横・上下に表示するアイコン */
  contentTitleIcon?: ContentTitleIconSettings;
  contentSubtitle?: string; // コンテンツサブタイトル
  contentPreInputNotice?: string; // 初期表示で入力前に表示する注意喚起（Markdownリンク可）
  contentPreInputNoticeAlign?: 'left' | 'center' | 'right'; // 注意喚起テキスト配置
  contentPreInputNoticePaddingX?: number; // 注意喚起テキスト左右パディング(px)
  contentPreInputNoticeRequireConsent?: boolean; // 入力前の同意チェックを必須にする
  contentPreInputNoticeConsentLabel?: string; // 同意チェックの文言（単一言語）
  contentPreInputNoticeConsentPlaceholder?: string; // 未同意時に入力欄に表示するプレースホルダー（単一言語）
  contentAlwaysInputNotice?: string; // メッセージ入力への注意文（常時表示）
  speechInputEnabled?: boolean; // 音声入力（マイクボタン）を表示するか
  /**
   * 公開AI検索の会話履歴と新規会話ボタンを表示するか
   * 未設定は OFF。設定画面・公開UIとも Local / Staging のみ。Production では常に非表示。
   */
  sessionHistoryEnabled?: boolean;
  contentPlaceholder?: string; // コンテンツ入力欄のプレースホルダー
  contentDisclaimer?: string; // 回答ページ表示時のアナウンス
  keywordsLabel?: string; // Keywords（キーワード候補）表示エリアの見出し/説明（改行可）
  /**
   * 公開AI検索ページのテキストサイズ切替
   * - textSizeControlEnabled: UIを表示するか
   * - textSizeDefault: 初期選択
   * - textSizeLabels: 表示ラベル（現状は日本語「小/中/大」を保存して使用。将来は languages/{lang} 側にも保存可能）
   */
  textSizeControlEnabled?: boolean;
  textSizeDefault?: 'small' | 'medium' | 'large' | 'xlarge';
  textSizeLabels?: {
    small: string;
    medium: string;
    large: string;
    xlarge: string;
  };
  /**
   * 公開画面（AI検索UI）の本文テキスト色
   * - mainTextColor: メインの本文テキスト色
   * - subTextColor: 補助/サブテキスト色
   */
  mainTextColor?: string;
  subTextColor?: string;
  /**
   * キーワード一覧（Leading Words）のテキスト色
   */
  keywordTextColor?: string;
  footerBackgroundColor?: string; // フッター背景色
  footerHasBorderTop?: boolean; // フッター上の線の有無
  footerBorderTopColor?: string; // フッター上の線の色
  footerText?: string; // フッターテキスト
  footerTextColor?: string; // フッターテキストの色
  isMultilingual?: boolean; // 多言語化されているか
  defaultLanguage?: string; // デフォルト言語
  availableLanguages?: string[]; // 利用可能な言語リスト
  /** @deprecated 後方互換用。textFontEn / textFontJp を優先 */
  textFont?: string;
  /** 英語フォント（プライマリ、先に適用） */
  textFontEn?: string;
  /** 日本語フォント */
  textFontJp?: string;
  updatedAt?: any; // Firestore Timestamp
}

// 公開サイト設定の保存
export const savePublicSiteSettings = async (workspaceId: string, settings: Partial<PublicSiteSettings>) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.savePublicSiteSettings' });
  const settingsRef = doc(db, 'publicPages', workspaceId, 'settings', 'site');

  const dataToSave = {
    ...settings,
    updatedAt: serverTimestamp()
  };

  console.log('💾 savePublicSiteSettings: Firestoreに設定を保存します', {
    workspaceId,
    path: `publicPages/${workspaceId}/settings/site`,
    logoUrl: settings.logoUrl || 'None',
    hasLogoUrl: !!settings.logoUrl,
    title: settings.title || 'None',
    hasTitle: !!settings.title,
    favicon: settings.favicon || 'None',
    hasFavicon: !!settings.favicon,
    footerBackgroundColor: settings.footerBackgroundColor || 'None',
    footerHasBorderTop: settings.footerHasBorderTop ?? 'None',
    footerBorderTopColor: settings.footerBorderTopColor || 'None',
    footerText: settings.footerText || 'None',
    footerTextColor: settings.footerTextColor || 'None',
    allKeys: Object.keys(settings)
  });

  await setDoc(settingsRef, dataToSave, { merge: true });

  console.log('✅ savePublicSiteSettings: 保存完了', {
    workspaceId,
    path: `publicPages/${workspaceId}/settings/site`
  });

  return { success: true };
};

// 公開サイト設定の読み込み
export const getPublicSiteSettings = async (workspaceId: string): Promise<PublicSiteSettings | null> => {
  const settingsRef = doc(db, 'publicPages', workspaceId, 'settings', 'site');
  const settingsSnap = await getDoc(settingsRef);

  if (settingsSnap.exists()) {
    const data = settingsSnap.data() as PublicSiteSettings;
    return data;
  } else {
    return null;
  }
};

// 多言語対応への移行
export const migrateToMultilingual = async (
  workspaceId: string, 
  languageCode: string,
  autoTranslate: boolean = false,
  options?: {
    /**
     * 既に追加済みの言語ドキュメントに欠落フィールドがある場合に補完する
     */
    backfillMissing?: boolean;
  }
) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.migrateToMultilingual' });
  const fn = httpsCallable(functions, 'migrateToMultilingualFn');
  const res = await fn({ workspaceId, languageCode, autoTranslate, ...(options ?? {}) });
  return res.data as {
    success: boolean;
    message: string;
    languageCode?: string;
    error?: string;
  };
};

// 言語固有設定の保存
export const saveLanguageSpecificSettings = async (
  workspaceId: string,
  languageCode: string,
  settings: {
    tabTitle?: string;
    title?: string;
    logoUrl?: string;
    contentTitle?: string;
    contentSubtitle?: string;
    contentPreInputNotice?: string;
    contentPreInputNoticeAlign?: 'left' | 'center' | 'right';
    contentPreInputNoticePaddingX?: number;
    contentPreInputNoticeRequireConsent?: boolean;
    contentPreInputNoticeConsentLabel?: string;
    contentPreInputNoticeConsentPlaceholder?: string;
    contentAlwaysInputNotice?: string;
    contentPlaceholder?: string;
    contentDisclaimer?: string;
    keywordsLabel?: string;
    contentTitleIcon?: ContentTitleIconSettings;
    textSizeLabels?: {
      small: string;
      medium: string;
      large: string;
      xlarge: string;
    };
    footerText?: string;
  }
) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.saveLanguageSpecificSettings' });
  const languageRef = doc(db, 'publicPages', workspaceId, 'settings', 'site', 'languages', languageCode);

  // undefinedの値を除外（Firestoreはundefinedを保存できない）
  const cleanedSettings = Object.fromEntries(
    Object.entries(settings).filter(([_, value]) => value !== undefined)
  );

  const dataToSave = {
    ...cleanedSettings,
    updatedAt: serverTimestamp()
  };

  console.log('💾 saveLanguageSpecificSettings: 言語固有設定を保存', {
    workspaceId,
    languageCode,
    path: `publicPages/${workspaceId}/settings/site/languages/${languageCode}`,
    fields: Object.keys(cleanedSettings)
  });

  await updateDoc(languageRef, dataToSave);

  console.log('✅ saveLanguageSpecificSettings: 保存完了');

  return { success: true };
};

// メイン言語を変更
export const changeMainLanguage = async (
  workspaceId: string,
  newMainLanguage: string
) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.changeMainLanguage' });
  const fn = httpsCallable(functions, 'changeMainLanguageFn');
  const res = await fn({ workspaceId, newMainLanguage });
  return res.data as {
    success: boolean;
    message: string;
  };
};

// 言語を削除
export const deleteLanguage = async (
  workspaceId: string,
  languageCode: string
) => {
  assertWorkspaceInvariant(workspaceId, { source: 'publicPages.deleteLanguage' });
  const fn = httpsCallable(functions, 'deleteLanguageFn');
  const res = await fn({ workspaceId, languageCode });
  return res.data as {
    success: boolean;
    message: string;
  };
};

/**
 * 多言語設定を取得（公開設定 + DRS設定をマージ）
 */
export interface MultilingualSettings {
  isMultilingual: boolean;
  defaultLanguage: string;
  availableLanguages: string[];
  source: 'site' | 'drs' | 'merged' | 'none';
}

export const getMultilingualSettings = async (workspaceId: string): Promise<MultilingualSettings> => {
  // 読み取り専用のため workspaceInvariant チェックは不要
  try {
    // 公開設定（site）と DRS 設定を並列取得（片方が失敗してももう片方を使う）
    const siteSettingsRef = doc(db, `publicPages/${workspaceId}/settings/site`);
    const drsSettingsRef = doc(db, `publicPages/${workspaceId}/settings/drs`);
    const [siteSettingsSnap, drsSettingsSnap] = await Promise.all([
      getDoc(siteSettingsRef).catch((e) => {
        console.warn('⚠️ [getMultilingualSettings] site 設定の取得に失敗:', e);
        return null;
      }),
      getDoc(drsSettingsRef).catch((e) => {
        console.warn('⚠️ [getMultilingualSettings] drs 設定の取得に失敗:', e);
        return null;
      })
    ]);
    
    let siteMultilingual = false;
    let siteDefaultLang = 'ja';
    let siteAvailableLangs: string[] = [];
    
    if (siteSettingsSnap && siteSettingsSnap.exists()) {
      const siteData = siteSettingsSnap.data();
      siteMultilingual = siteData.isMultilingual || false;
      siteDefaultLang = siteData.defaultLanguage || 'ja';
      siteAvailableLangs = siteData.availableLanguages || [];
    }
    
    let drsMultilingual = false;
    let drsDefaultLang = 'ja';
    let drsAvailableLangs: string[] = [];
    
    if (drsSettingsSnap && drsSettingsSnap.exists()) {
      const drsData = drsSettingsSnap.data();
      drsMultilingual = drsData.isMultilingual || false;
      drsDefaultLang = drsData.defaultLanguage || 'ja';
      drsAvailableLangs = drsData.availableLanguages || [];
    }
    
    // マージロジック
    if (!siteMultilingual && !drsMultilingual) {
      // どちらも多言語対応していない
      return {
        isMultilingual: false,
        defaultLanguage: 'ja',
        availableLanguages: [],
        source: 'none'
      };
    }
    
    if (siteMultilingual && !drsMultilingual) {
      // 公開設定のみ多言語対応
      return {
        isMultilingual: true,
        defaultLanguage: siteDefaultLang,
        availableLanguages: siteAvailableLangs,
        source: 'site'
      };
    }
    
    if (!siteMultilingual && drsMultilingual) {
      // DRS設定のみ多言語対応
      return {
        isMultilingual: true,
        defaultLanguage: drsDefaultLang,
        availableLanguages: drsAvailableLangs,
        source: 'drs'
      };
    }
    
    // 両方とも多言語対応している場合はマージ
    const mergedLangs = Array.from(new Set([...siteAvailableLangs, ...drsAvailableLangs]));
    
    // デフォルト言語は公開設定を優先
    const defaultLang = siteDefaultLang || drsDefaultLang;
    
    return {
      isMultilingual: true,
      defaultLanguage: defaultLang,
      availableLanguages: mergedLangs,
      source: 'merged'
    };
  } catch (error) {
    console.error('❌ [getMultilingualSettings] エラー:', error);
    return {
      isMultilingual: false,
      defaultLanguage: 'ja',
      availableLanguages: [],
      source: 'none'
    };
  }
};
